import React from 'react';
import { X, DollarSign, TrendingDown, AlertTriangle, CheckCircle } from 'lucide-react';
import { Loan } from '../types';
import { LOAN_OPTIONS } from '../constants';

interface LoansModalProps {
  onClose: () => void;
  money: number;
  loans: Loan[];
  gameDay: number;
  onTakeLoan: (optionId: string) => void;
  onRepayLoan: (loanId: string) => void;
  formatCurrency: (n: number) => string;
}

const LoansModal: React.FC<LoansModalProps> = ({
  onClose, money, loans, gameDay, onTakeLoan, onRepayLoan, formatCurrency,
}) => {
  const activeLoans = loans.filter(l => !l.paidOff);
  const totalDebt = activeLoans.reduce((s, l) => s + l.remainingBalance, 0);
  const totalDailyInterest = activeLoans.reduce((s, l) => s + l.principal * l.annualRate / 365, 0);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <DollarSign size={18} className="text-green-400" />
            <h2 className="text-white font-bold text-lg">Loans & Financing</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Summary bar */}
          {activeLoans.length > 0 && (
            <div className="flex gap-4 text-sm bg-gray-800 rounded-lg p-3">
              <div>
                <div className="text-gray-400">Total Outstanding</div>
                <div className="text-red-400 font-bold">{formatCurrency(totalDebt)}</div>
              </div>
              <div className="border-l border-gray-700 pl-4">
                <div className="text-gray-400">Daily Interest</div>
                <div className="text-orange-400 font-bold">−{formatCurrency(totalDailyInterest)}/day</div>
              </div>
              <div className="border-l border-gray-700 pl-4">
                <div className="text-gray-400">Active Loans</div>
                <div className="text-white font-bold">{activeLoans.length}</div>
              </div>
            </div>
          )}

          {/* Active loans */}
          {activeLoans.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Active Loans</h3>
              <div className="space-y-3">
                {activeLoans.map(loan => {
                  const dailyInterest = loan.principal * loan.annualRate / 365;
                  const daysRemaining = Math.max(0, loan.startDay + loan.termDays - gameDay);
                  const progress = Math.min(1, (gameDay - loan.startDay) / loan.termDays);
                  const canRepay = money >= loan.remainingBalance;
                  return (
                    <div key={loan.id} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold text-white text-sm">{loan.label}</div>
                        <div className="text-xs text-gray-400">{daysRemaining} days remaining</div>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-1.5 mb-3">
                        <div className="bg-orange-500 h-1.5 rounded-full" style={{ width: `${progress * 100}%` }} />
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                        <span>Outstanding: <span className="text-red-400 font-medium">{formatCurrency(loan.remainingBalance)}</span></span>
                        <span>Interest: <span className="text-orange-400 font-semibold">−{formatCurrency(dailyInterest)}/day</span></span>
                        <span>{(loan.annualRate / 12 * 100).toFixed(0)}%/month</span>
                      </div>
                      <button
                        onClick={() => onRepayLoan(loan.id)}
                        disabled={!canRepay}
                        className={`w-full py-1.5 rounded text-xs font-medium transition-colors ${
                          canRepay
                            ? 'bg-green-700 hover:bg-green-600 text-white'
                            : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {canRepay
                          ? `Repay Now — ${formatCurrency(loan.remainingBalance)}`
                          : `Need ${formatCurrency(loan.remainingBalance - money)} more to repay`}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Available loan options */}
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Available Financing</h3>
            <div className="space-y-3">
              {LOAN_OPTIONS.map(opt => {
                const dailyInterest = opt.principal * opt.annualRate / 365;
                const totalInterest = dailyInterest * opt.termDays;
                const alreadyHave = activeLoans.some(l => l.label === opt.label);
                return (
                  <div key={opt.id} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-white text-sm">{opt.label}</span>
                          <span className="text-xs text-gray-400 bg-gray-700 px-1.5 py-0.5 rounded">{opt.termDays}d term</span>
                        </div>
                        <p className="text-xs text-gray-400 mb-2">{opt.description}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                          <span>Principal: <span className="text-green-400 font-medium">{formatCurrency(opt.principal)}</span></span>
                          <span>Rate: <span className="text-orange-400">{(opt.annualRate / 12 * 100).toFixed(0)}%/month</span></span>
                          <span>Daily cost: <span className="text-red-400 font-semibold">−{formatCurrency(dailyInterest)}/day</span></span>
                          <span>Total interest over {opt.termDays}d: <span className="text-red-300 font-semibold">{formatCurrency(totalInterest)}</span></span>
                        </div>
                      </div>
                      <button
                        onClick={() => onTakeLoan(opt.id)}
                        disabled={alreadyHave}
                        className={`shrink-0 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          alreadyHave
                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            : 'bg-green-700 hover:bg-green-600 text-white'
                        }`}
                      >
                        {alreadyHave ? 'Active' : 'Take Loan'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 text-xs text-amber-400 bg-amber-900/20 border border-amber-700/40 rounded-lg p-3">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>These are high-interest datacenter financing products, not retail loans. Rates reflect in-game time (1 in-game day = 6 real minutes). Daily interest is deducted from your cash balance every tick. Make sure your daily revenue comfortably exceeds the interest cost before borrowing. Repay early at any time to stop the clock.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoansModal;
